import { fetchAuthSession } from 'aws-amplify/auth';
import profileImageCache from './profileImageCache';

class UserService {
  constructor() {
    this.userInfo = null;
    this._currentUserPromise = null;
    this._permissionsCache = null;
    this._permissionsPromise = null;
    this._tenantVerticalPromise = null;
  }

  normalizeVerticalKey(value) {
    const raw = String(value || '').trim().toLowerCase();
    const key = raw
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return { raw, key };
  }

  isFnbVerticalValue(value) {
    const { raw, key } = this.normalizeVerticalKey(value);
    if (!raw) return false;
    // Canonical backend value for F&B is "restaurant"
    return key === 'restaurant';
  }

  async getTenantVertical() {
    // Cache the in-flight promise so multiple callers don't spam /tenant.
    if (this._tenantVerticalPromise) {
      return this._tenantVerticalPromise;
    }

    this._tenantVerticalPromise = (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) return null;

        const apiUrl =
          process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';
        const res = await fetch(`${apiUrl}/tenant`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) return null;
        const tenant = await res.json().catch(() => null);
        const vertical =
          tenant?.data?.business_vertical ??
          tenant?.business_vertical ??
          tenant?.data?.vertical ??
          tenant?.vertical ??
          null;
        return vertical != null ? String(vertical) : null;
      } catch (e) {
        return null;
      } finally {
        // Allow refresh on next call (avoid staleness across sign-in/out).
        this._tenantVerticalPromise = null;
      }
    })();

    return this._tenantVerticalPromise;
  }

  async isFnbTenant() {
    const vertical = await this.getTenantVertical();
    return this.isFnbVerticalValue(vertical);
  }

  async getCurrentUser() {
    if (this.userInfo) {
      return this.userInfo;
    }

    if (this._currentUserPromise) {
      return this._currentUserPromise;
    }

    this._currentUserPromise = this._loadCurrentUser();
    try {
      return await this._currentUserPromise;
    } finally {
      this._currentUserPromise = null;
    }
  }

  async _loadCurrentUser() {
    try {
      const { tokens } = await fetchAuthSession();

      if (tokens && tokens.idToken) {
        const idToken = tokens.idToken;
        const payload = idToken.payload;

        // Get basic info from token
        const userId = payload.sub || payload['cognito:username'];
        const userName =
          payload.name || payload.given_name || payload['cognito:username'];

        // Try to make API call to get detailed user information
        let userDetails = null;
        try {
          userDetails = await this.fetchUserDetails(
            userId,
            tokens.idToken.toString()
          );
        } catch (apiError) {
          console.warn('⚠️ API call failed, using token data:', apiError);
          // Fallback to token data if API fails
          userDetails = {
            role: payload['custom:role'] || payload.role || 'user',
            displayName:
              payload['custom:spec_role'] ||
              payload.spec_role ||
              payload['custom:role'] ||
              payload.role ||
              'User',
            specRole:
              payload['custom:spec_role'] || payload.spec_role || 'guard',
            companyName: payload['custom:company_name'] || 'Innoworx',
            tenantId: payload['custom:tenant_id'] || payload.tenant_id,
          };
        }

        // If API call succeeded, use the full userDetails object
        if (userDetails) {
          this.userInfo = userDetails;
          return userDetails;
        } else {
          // Fallback to basic token data if API failed
          const userInfo = {
            userId: userId,
            userName: userName,
            email: payload.email,
            role: 'user',
            displayName: 'User',
            companyName: 'Innoworx',
            tenantId: null,
            specRole: 'guard',
          };
          this.userInfo = userInfo;
          return userInfo;
        }
      }

      return null;
    } catch (error) {
      console.error('Error fetching user information:', error);
      return null;
    }
  }

  async fetchUserDetails(userId, idToken) {
    try {
      // Use the user/me endpoint
      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';
      console.log('🔍 Making API call to:', `${apiUrl}/user/me`);
      console.log('🔍 Using ID Token:', idToken.substring(0, 50) + '...');
      const response = await fetch(`${apiUrl}/user/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      console.log('🔍 API Response status:', response.status);

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const userData = await response.json();
      console.log('🔍 User details from API:', userData);

      // Get role display name - prioritize spec_role first, then role_name
      let roleDisplayName = 'User';

      if (userData.spec_role && userData.spec_role.trim() !== '') {
        // If spec_role exists, try to make API call to get display name
        try {
          const roleResponse = await fetch(
            `${apiUrl}/user/roles/${userData.spec_role}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (roleResponse.ok) {
            const roleData = await roleResponse.json();
            roleDisplayName =
              roleData.display_name || roleData.role_name || userData.spec_role;
            console.log(
              '🔍 Role display name from API for spec_role:',
              roleDisplayName
            );
          } else {
            console.warn('⚠️ Role API returned status:', roleResponse.status);
            // Use role_name if available, otherwise use spec_role
            roleDisplayName = userData.role_name || userData.spec_role;
            console.log('🔍 Using role_name as fallback:', roleDisplayName);
          }
        } catch (roleError) {
          console.warn('⚠️ Could not fetch role display name:', roleError);
          // Use role_name if available, otherwise use spec_role
          roleDisplayName = userData.role_name || userData.spec_role;
          console.log(
            '🔍 Using role_name as fallback after error:',
            roleDisplayName
          );
        }
      } else if (userData.role_name && userData.role_name.trim() !== '') {
        // If spec_role is empty but role_name exists, use it directly (it's already in proper case)
        roleDisplayName = userData.role_name;
        console.log(
          '🔍 Using role_name directly (proper case):',
          roleDisplayName
        );
      }

      // Map the API response to our expected format, including all available fields
      return {
        // Basic info
        userId: userData.user_id || userId,
        userName: userData.user_name || userData.name || 'User',
        email: userData.email || userData.phone_number || '',
        role: userData.role_name || userData.spec_role || 'user',
        displayName: roleDisplayName,
        specRole: userData.spec_role || userData.role_name || 'guard',
        companyName: userData.company_name || 'Innoworx',
        tenantId: userData.tenant_id,

        // Available profile fields from User table
        user_name: userData.user_name,
        employee_id: userData.employee_id,
        phone_number: userData.phone_number,
        role_name: userData.role_name,
        spec_role: userData.spec_role,
        reports_to: userData.reports_to,
        image_path: userData.image_path,
        image_presigned_url: userData.image_presigned_url,
        gender: userData.gender,
        date_of_birth: userData.date_of_birth,
        blood_group: userData.blood_group,
        address: userData.address,
        location: userData.location,
        total_assigned_shifts: userData.total_assigned_shifts,
        total_present_shifts: userData.total_present_shifts,
      };
    } catch (error) {
      console.error('Error fetching user details from API:', error);
      // Return fallback data if API call fails
      return {
        role: 'user',
        displayName: 'User',
        specRole: 'guard',
        companyName: 'Innoworx',
      };
    }
  }

  async getUserPermissions() {
    if (this._permissionsCache) {
      return this._permissionsCache;
    }

    if (this._permissionsPromise) {
      return this._permissionsPromise;
    }

    this._permissionsPromise = this._fetchUserPermissions();
    try {
      const permissions = await this._permissionsPromise;
      this._permissionsCache = permissions;
      return permissions;
    } finally {
      this._permissionsPromise = null;
    }
  }

  async _fetchUserPermissions() {
    try {
      const { tokens } = await fetchAuthSession();
      if (!tokens || !tokens.idToken) {
        console.warn('No authentication token available for permissions');
        return [];
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';
      
      // Get role name; prefer user object, but fall back to token payload
      const user = await this.getCurrentUser();
      const payload = tokens.idToken.payload || {};

      let roleName =
        user?.spec_role ||
        user?.specRole ||
        user?.role_name ||
        user?.roleName ||
        user?.role ||
        payload['custom:spec_role'] ||
        payload.spec_role ||
        payload['custom:role'] ||
        payload.role ||
        null;
      
      if (!roleName) {
        console.warn('⚠️ No role name found for user, cannot fetch permissions');
        console.warn('User object:', {
          spec_role: user?.spec_role,
          role_name: user?.role_name,
          role: user?.role,
        });
        return [];
      }

      // Normalize role name - try lowercase first (most common)
      const normalizedRoleName = roleName.toLowerCase();
      console.log(`🔍 Attempting to fetch permissions for role: "${roleName}" (normalized: "${normalizedRoleName}")`);

      try {
        // Try with normalized (lowercase) role name first
        let response = await fetch(
          `${apiUrl}/user/roles/permissions?role_name=${encodeURIComponent(normalizedRoleName)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${tokens.idToken.toString()}`,
              'Content-Type': 'application/json',
            },
          }
        );

        // If 404, try with original case
        if (response.status === 404 && normalizedRoleName !== roleName) {
          console.log(`⚠️ Role "${normalizedRoleName}" not found, trying original case: "${roleName}"`);
          response = await fetch(
            `${apiUrl}/user/roles/permissions?role_name=${encodeURIComponent(roleName)}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${tokens.idToken.toString()}`,
                'Content-Type': 'application/json',
              },
            }
          );
        }

        if (response.status === 200) {
          const data = await response.json();
          // Extract effective_permissions from response
          const permissions = data?.effective_permissions || data?.body?.effective_permissions || data?.data?.effective_permissions || [];
          console.log('✅ Fetched permissions from API:', permissions);
          return Array.isArray(permissions) ? permissions : [];
        } else {
          const errorBody = await response.json().catch(() => ({}));
          const errorMessage = errorBody?.error || errorBody?.message || 'Unknown error';
          console.error(`❌ Permissions API returned status: ${response.status} - ${errorMessage}`);
          console.error(`❌ Attempted role names: "${normalizedRoleName}" and "${roleName}"`);
          console.error('User role info:', { spec_role: user?.spec_role, role_name: user?.role_name, role: user?.role });
          return [];
        }
      } catch (apiError) {
        console.error('❌ Error fetching permissions from API:', apiError);
        return [];
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return [];
    }
  }

  /**
   * Get permissions for a specific role (useful for checking what permissions a role has)
   * @param {string} roleName - The role name to check (e.g., 'admin', 'supervisor', 'guard', 'driver')
   * @returns {Promise<Array<string>>} Array of permission strings for that role
   */
  async getRolePermissions(roleName) {
    try {
      if (!roleName) {
        console.warn('No role name provided');
        return [];
      }

      const { tokens } = await fetchAuthSession();
      if (!tokens || !tokens.idToken) {
        console.warn('No authentication token available for permissions');
        return [];
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

      // Normalize role name - try lowercase first (most common)
      const normalizedRoleName = roleName.toLowerCase();
      console.log(`🔍 Attempting to fetch permissions for role: "${roleName}" (normalized: "${normalizedRoleName}")`);

      try {
        // Try with normalized (lowercase) role name first
        let response = await fetch(
          `${apiUrl}/user/roles/permissions?role_name=${encodeURIComponent(normalizedRoleName)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${tokens.idToken.toString()}`,
              'Content-Type': 'application/json',
            },
          }
        );

        // If 404, try with original case
        if (response.status === 404 && normalizedRoleName !== roleName) {
          console.log(`⚠️ Role "${normalizedRoleName}" not found, trying original case: "${roleName}"`);
          response = await fetch(
            `${apiUrl}/user/roles/permissions?role_name=${encodeURIComponent(roleName)}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${tokens.idToken.toString()}`,
                'Content-Type': 'application/json',
              },
            }
          );
        }

        if (response.status === 200) {
          const data = await response.json();
          // Extract effective_permissions from response
          const permissions = data?.effective_permissions || data?.body?.effective_permissions || data?.data?.effective_permissions || [];
          console.log(`✅ Fetched permissions for role "${roleName}":`, permissions);
          return Array.isArray(permissions) ? permissions : [];
        } else {
          const errorBody = await response.json().catch(() => ({}));
          const errorMessage = errorBody?.error || errorBody?.message || 'Unknown error';
          console.error(`❌ Permissions API returned status: ${response.status} - ${errorMessage}`);
          console.error(`❌ Attempted role names: "${normalizedRoleName}" and "${roleName}"`);
          return [];
        }
      } catch (apiError) {
        console.error(`❌ Error fetching permissions for role "${roleName}":`, apiError);
        return [];
      }
    } catch (error) {
      console.error(`Error fetching permissions for role "${roleName}":`, error);
      return [];
    }
  }

  async getS3PresignedUrl(s3Path) {
    try {
      if (!s3Path) return null;

      // Check cache first - use s3Path as the key
      const cachedUrl = profileImageCache.get(s3Path, true);
      if (cachedUrl) {
        return cachedUrl;
      }

      const { tokens } = await fetchAuthSession();
      if (!tokens || !tokens.idToken) {
        throw new Error('No authentication token available');
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';
      const response = await fetch(
        `${apiUrl}/s3-presigned?url=${encodeURIComponent(s3Path)}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${tokens.idToken.toString()}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get presigned URL: ${response.status}`);
      }

      const data = await response.json();
      const presignedUrl = data.presigned_url;
      
      // Cache the presigned URL with s3Path as key
      if (presignedUrl) {
        profileImageCache.set(s3Path, presignedUrl, s3Path, true);
      }
      
      return presignedUrl;
    } catch (error) {
      console.error('Error getting S3 presigned URL:', error);
      return null;
    }
  }

  async getProfileImageUrl(userId) {
    try {
      if (!userId) return null;

      // Check cache first
      const cachedUrl = profileImageCache.get(userId);
      if (cachedUrl) {
        return cachedUrl;
      }

      // Check if we have a cached S3 path for this user
      const cachedS3Path = profileImageCache.getS3Path(userId);
      if (cachedS3Path) {
        // Try to get presigned URL from cached S3 path
        const presignedUrl = await this.getS3PresignedUrl(cachedS3Path);
        if (presignedUrl) {
          // Cache the final URL with userId as key
          profileImageCache.set(userId, presignedUrl, cachedS3Path);
          return presignedUrl;
        }
      }

      const { tokens } = await fetchAuthSession();
      if (!tokens || !tokens.idToken) {
        throw new Error('No authentication token available');
      }

      const apiUrl =
        process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';
      const response = await fetch(
        `${apiUrl}/user/profile-picture?user_id=${userId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${tokens.idToken.toString()}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch profile image: ${response.status}`);
      }

      const data = await response.json();

      // Handle both response formats for backward compatibility
      if (data.image_path) {
        console.log('🖼️ Got S3 path, fetching presigned URL:', data.image_path);
        
        // Cache the S3 path first
        profileImageCache.setS3Path(userId, data.image_path);
        
        // Get presigned URL (this will also cache it)
        const presignedUrl = await this.getS3PresignedUrl(data.image_path);
        
        // Cache the final URL with userId as key
        if (presignedUrl) {
          profileImageCache.set(userId, presignedUrl, data.image_path);
        }
        
        return presignedUrl;
      } else if (data.profile_image_url) {
        console.log('🖼️ Got direct profile image URL:', data.profile_image_url);
        // Cache direct URL
        profileImageCache.set(userId, data.profile_image_url);
        return data.profile_image_url;
      }

      return null;
    } catch (error) {
      console.error('Error fetching profile image:', error);
      return null;
    }
  }

  clearUserInfo() {
    this.userInfo = null;
    this._currentUserPromise = null;
    this._permissionsCache = null;
    this._permissionsPromise = null;
  }
}

export default new UserService();
